import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdPsychology } from 'react-icons/md';
import { useBackoffice } from '@contexts/useBackoffice';
import {
  LayoutWrapper,
  Sidebar,
  SidebarHeader,
  SidebarBrand,
  SidebarTitleGroup,
  SidebarTitle,
  SidebarSubtitle,
  SidebarLogo,
  NavItem,
  NavIcon,
  NavSection,
  MainContent,
  FlexSpacer,
  CollapseButton,
} from './BackofficeLayout.styled';
import type { NavEntry, BackofficeLayoutProps } from './types';

const NAV_ITEMS: NavEntry[] = [
  { path: '/backoffice-admin/organizations', label: 'Organizations', icon: 'corporate_fare' },
  { path: '/backoffice-admin/users', label: 'Users', icon: 'person_0' },
  { path: '/backoffice-admin/billing', label: 'Billing & Credits', icon: 'paid_0' },
  { path: '/backoffice-admin/memory', label: 'Memory', icon: '', iconComponent: MdPsychology },
];

const BackofficeLayout: React.FC<BackofficeLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useBackoffice();

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  return (
    <LayoutWrapper>
      <Sidebar $collapsed={collapsed}>
        <SidebarHeader>
          {!collapsed && (
            <SidebarBrand>
              <SidebarLogo src="/img/icons/logo.svg" alt="Aibuild" />
              <SidebarTitleGroup>
                <SidebarTitle>Aibuild OS</SidebarTitle>
                <SidebarSubtitle>Backoffice</SidebarSubtitle>
              </SidebarTitleGroup>
            </SidebarBrand>
          )}
          <CollapseButton onClick={() => setCollapsed((c) => !c)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              {collapsed ? (
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              ) : (
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              )}
            </svg>
          </CollapseButton>
        </SidebarHeader>
        {!collapsed && (
          <>
            <NavSection>
              {NAV_ITEMS.map((item) => (
                <NavItem
                  key={item.path}
                  $active={location.pathname.startsWith(item.path)}
                  onClick={() => handleNav(item.path)}
                >
                  <NavIcon>
                    {item.iconComponent ? (
                      <item.iconComponent size={18} />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <use href={`#${item.icon}`} />
                      </svg>
                    )}
                  </NavIcon>
                  {item.label}
                </NavItem>
              ))}
            </NavSection>
            <FlexSpacer />
            <NavSection>
              <NavItem onClick={() => navigate('/')}>
                <NavIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <use href="#arrow_back_0" />
                  </svg>
                </NavIcon>
                Back to App
              </NavItem>
              <NavItem onClick={handleLogout}>
                <NavIcon>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <use href="#lock_0" />
                  </svg>
                </NavIcon>
                Lock Backoffice
              </NavItem>
            </NavSection>
          </>
        )}
      </Sidebar>
      <MainContent>{children}</MainContent>
    </LayoutWrapper>
  );
};

export default BackofficeLayout;
